import {
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import table from '../materials/Materials.module.css';
import { errorCode } from '../personnel/source.js';
import classes from './Purchasing.module.css';
import { formatAmount, formatQuantity, numberOf, numberSeparators } from './requisitions.js';
import {
  emptyQuotationForm,
  type QuotationFormValues,
  quotationFormFrom,
  quotationFormTotals,
  toQuotationInput,
  validateQuotationForm,
} from './rfqs.js';
import type { RfqSource } from './source.js';
import type { RfqDetail } from './types.js';

/**
 * Tedarikçi teklifi (`satin-alma-modulu.md` §6): her RFQ kalemine birim fiyat, teslim süresi,
 * geçerlilik, garanti ve toplam teslim maliyetinin kalemleri (indirim, navlun, gümrük, vergi,
 * hizmet bedeli). Toplam canlı hesaplanır; fiyatlanmayan kalem teklifi karşılaştırma dışı bırakır.
 */
export function QuotationDialog({
  rfq,
  supplier,
  today,
  source,
  onClose,
  onSaved,
}: {
  rfq: RfqDetail;
  supplier: { id: string; name: string };
  today: string;
  source: RfqSource;
  onClose: () => void;
  onSaved: (next: RfqDetail) => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`purchasing.rfq.quotation.${key}`, options);
  const existing = rfq.quotations.find((q) => q.supplierId === supplier.id);
  const [initial] = useState<QuotationFormValues>(() =>
    existing ? quotationFormFrom(existing, rfq) : emptyQuotationForm(rfq, supplier.id, today),
  );
  const [values, setValues] = useState<QuotationFormValues>(initial);
  // Girilmiş teklif sorulmadan atılmaz: kapatırken onay
  const [confirmClose, setConfirmClose] = useState(false);
  const dirty = JSON.stringify(values) !== JSON.stringify(initial);
  const requestClose = () => (dirty ? setConfirmClose(true) : onClose());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<QuotationFormValues>) => setValues((v) => ({ ...v, ...patch }));
  const error = (field: string) =>
    errors[field]
      ? t(`purchasing.field.${errors[field]}`, { defaultValue: errors[field] })
      : undefined;
  const totals = quotationFormTotals(values, rfq);
  const amountInput = (
    field: 'discount' | 'freight' | 'customs' | 'tax' | 'serviceFee',
    label: string,
  ) => (
    <NumberInput
      label={label}
      min={0}
      decimalScale={2}
      {...numberSeparators(locale)}
      value={values[field]}
      onChange={(value) => set({ [field]: value } as Partial<QuotationFormValues>)}
      error={error(field)}
    />
  );

  return (
    <Modal
      opened
      onClose={requestClose}
      size="xl"
      title={k(existing ? 'titleEdit' : 'titleNew', { supplier: supplier.name })}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <form
        noValidate
        onSubmit={async (event) => {
          event.preventDefault();
          const found = validateQuotationForm(values, rfq);
          setErrors(found);
          if (Object.keys(found).length > 0) return;
          setBusy(true);
          setSaveError(null);
          try {
            onSaved(await source.saveQuotation(rfq.id, supplier.id, toQuotationInput(values, rfq)));
          } catch (failure) {
            setSaveError(errorCode(failure));
          } finally {
            setBusy(false);
          }
        }}
      >
        <fieldset className={classes.dialogSection}>
          <legend>{k('header')}</legend>
          <div className={classes.dialogGrid}>
            <TextInput
              label={k('ref')}
              maxLength={80}
              value={values.quotationRef}
              onChange={(event) => set({ quotationRef: event.currentTarget.value })}
              data-autofocus
            />
            <DayInput
              label={k('received')}
              required
              maxDate={today}
              value={values.receivedDate}
              onChange={(value) => set({ receivedDate: value ?? '' })}
              error={error('receivedDate')}
            />
            <TextInput
              label={k('currency')}
              required
              maxLength={3}
              value={values.currency}
              onChange={(event) => set({ currency: event.currentTarget.value.toUpperCase() })}
              error={error('currency')}
            />
            <NumberInput
              label={k('leadTime')}
              min={0}
              allowDecimal={false}
              value={values.leadTimeDays}
              onChange={(value) => set({ leadTimeDays: value })}
              error={error('leadTimeDays')}
            />
            <NumberInput
              label={k('validity')}
              min={0}
              allowDecimal={false}
              value={values.validityDays}
              onChange={(value) => set({ validityDays: value })}
              error={error('validityDays')}
            />
            <TextInput
              label={k('warranty')}
              maxLength={200}
              value={values.warranty}
              onChange={(event) => set({ warranty: event.currentTarget.value })}
            />
          </div>
        </fieldset>

        <fieldset className={classes.dialogSection}>
          <legend>{k('prices')}</legend>
          <section
            className={table.tableWrap}
            aria-label={k('prices')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${classes.table} ${classes.lineTable}`}>
              <thead>
                <tr>
                  <th scope="col" className={classes.index}>
                    {t('purchasing.rfq.detail.columns.no')}
                  </th>
                  <th scope="col">{t('purchasing.rfq.detail.columns.material')}</th>
                  <th scope="col" data-numeric>
                    {t('purchasing.rfq.detail.columns.qty')}
                  </th>
                  <th scope="col">{t('purchasing.rfq.detail.columns.uom')}</th>
                  <th scope="col" data-numeric>
                    {k('unitPrice', { currency: values.currency || rfq.currency })}
                  </th>
                  <th scope="col">{k('free')}</th>
                  <th scope="col" data-numeric>
                    {k('lineTotal')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rfq.lines.map((line) => {
                  // Form değeri yazıldığı gibi; tutar sayısıyla (ara değer "1." → 1)
                  const price = values.prices[line.id] ?? '';
                  const unitPrice = numberOf(price);
                  // Ücretsiz / promosyon: fiyat 0 ve kilitli (ADR 0166 §1)
                  const free = values.free?.[line.id] === true;
                  return (
                    <tr key={line.id}>
                      <td className={classes.index}>{line.lineNo}</td>
                      <td className={classes.wrap}>{line.name}</td>
                      <td data-numeric>{formatQuantity(line.quantity, locale)}</td>
                      <td>{line.unit}</td>
                      <td data-numeric className={classes.price}>
                        <NumberInput
                          aria-label={k('priceOf', { name: line.name })}
                          min={0}
                          decimalScale={2}
                          {...numberSeparators(locale)}
                          hideControls
                          disabled={free}
                          value={price}
                          onChange={(value) =>
                            set({ prices: { ...values.prices, [line.id]: value } })
                          }
                          error={error(`price:${line.id}`)}
                        />
                      </td>
                      <td>
                        <Checkbox
                          aria-label={k('freeOf', { name: line.name })}
                          checked={free}
                          onChange={(event) => {
                            const checked = event.currentTarget.checked;
                            set({
                              free: { ...values.free, [line.id]: checked },
                              prices: { ...values.prices, [line.id]: checked ? 0 : '' },
                            });
                          }}
                        />
                      </td>
                      <td data-numeric>
                        {unitPrice === '' ? '—' : formatAmount(unitPrice * line.quantity, locale)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </fieldset>

        <fieldset className={classes.dialogSection}>
          <legend>{k('costs')}</legend>
          <div className={classes.dialogGrid}>
            {amountInput('discount', k('discount'))}
            {amountInput('freight', k('freight'))}
            {amountInput('customs', k('customs'))}
            {amountInput('tax', k('tax'))}
            {amountInput('serviceFee', k('serviceFee'))}
          </div>
          <Textarea
            label={k('remarks')}
            autosize
            minRows={2}
            maxLength={1000}
            value={values.remarks}
            onChange={(event) => set({ remarks: event.currentTarget.value })}
            mt="sm"
          />
          <dl className={classes.totals} aria-live="polite">
            <div>
              <dt>{k('itemsTotal')}</dt>
              <dd>
                {formatAmount(totals.itemsTotal, locale)} {values.currency}
              </dd>
            </div>
            <div data-strong>
              <dt>{k('landedTotal')}</dt>
              <dd>
                {formatAmount(totals.landedTotal, locale)} {values.currency}
              </dd>
            </div>
          </dl>
          {totals.complete ? null : (
            <p className={classes.notice} data-tone="info">
              {k('incomplete')}
            </p>
          )}
        </fieldset>

        {saveError ? (
          <p className={classes.error} role="alert">
            {t(`purchasing.errors.${saveError}`, { defaultValue: t('purchasing.errors.GENERIC') })}
          </p>
        ) : null}
        {confirmClose ? (
          <div className={classes.notice} data-tone="danger" role="alertdialog" aria-live="polite">
            <Text fz="sm" fw={600}>
              {t('common.unsaved.title')}
            </Text>
            <Text fz="sm">{t('common.unsaved.message')}</Text>
            <Group justify="flex-end" gap="sm" mt="xs">
              <Button variant="default" size="xs" onClick={() => setConfirmClose(false)}>
                {t('common.unsaved.stay')}
              </Button>
              <Button color="red" size="xs" onClick={onClose}>
                {t('common.unsaved.discard')}
              </Button>
            </Group>
          </div>
        ) : null}
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={requestClose}>
            {k('cancel')}
          </Button>
          <Button type="submit" loading={busy}>
            {k('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
